import React, { useMemo, useState } from 'react';
import { AlertTriangleIcon, CheckCircle2Icon, LockIcon } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Field, Input, Textarea } from '../ui/Field';
import { useCampaGift } from '../../contexts/CampaGiftContext';
import { applyBps, bpsLabel, koboToNaira, nairaToKobo } from '../../utils/campagift';
interface BuyGiftDialogProps {
  open: boolean;
  typeId: string | null;
  onClose: () => void;
}
type Step = 'compose' | 'review' | 'payment' | 'issued';

/**
 * Purchase → review → payment confirmation → issuance.
 *
 * Issuance is NEVER triggered by the confirm button. The flow waits on an
 * authoritative provider confirmation, and that capability is gated, so the
 * real-money path stops here and says so.
 */
export function BuyGiftDialog({
  open,
  typeId,
  onClose
}: BuyGiftDialogProps) {
  const {
    types,
    issue,
    flags,
    merchants
  } = useCampaGift();
  const [step, setStep] = useState<Step>('compose');
  const [amount, setAmount] = useState('');
  const [recipient, setRecipient] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [issuedCode, setIssuedCode] = useState<string | null>(null);
  const [issuedReference, setIssuedReference] = useState<string | null>(null);
  const type = types.find(t => t.id === typeId) ?? null;
  const purchaseEnabled = flags.find(f => f.key === 'campa_gift_purchase')?.enabled ?? false;
  const kobo = useMemo(() => amount ? nairaToKobo(amount) : null, [amount]);
  const fee = type && kobo ? applyBps(kobo, type.purchaseFeeBps) : 0;
  const total = (kobo ?? 0) + fee;
  function reset() {
    setStep('compose');
    setAmount('');
    setRecipient('');
    setMessage('');
    setError(null);
    setIssuedCode(null);
    setIssuedReference(null);
  }
  function close() {
    reset();
    onClose();
  }
  function toReview() {
    if (!type) return;
    if (kobo === null) {
      setError('Enter an amount, for example 10000 or 10000.50.');
      return;
    }
    if (kobo < type.minValueKobo || kobo > type.maxValueKobo) {
      setError(`${type.name} accepts between ${koboToNaira(type.minValueKobo)} and ${koboToNaira(type.maxValueKobo)}.`);
      return;
    }
    if (!recipient.replace(/^@/, '').trim()) {
      setError('Choose a recipient by Campuzon handle.');
      return;
    }
    setError(null);
    setStep('review');
  }

  /** Stands in for the provider webhook confirming a real payment server-side. */
  function simulateProviderConfirmation() {
    if (!type || kobo === null) return;
    const result = issue({
      typeId: type.id,
      recipientHandle: recipient,
      faceValueKobo: kobo,
      message,
      paymentConfirmed: true
    });
    if (!result.ok) {
      setError(result.error);
      setStep('review');
      return;
    }
    setIssuedCode(result.code);
    setIssuedReference(result.gift.publicReference);
    setStep('issued');
  }
  if (!type) return null;
  const eligibleMerchants = merchants.filter(m => m.campaGiftEnabled && m.participation === 'active' && m.eligibleTypeIds.includes(type.id));
  return <Modal open={open} onClose={close} title={step === 'issued' ? 'CampaGift issued' : `Buy ${type.name}`} description={step === 'issued' ? undefined : type.description} footer={step === 'compose' ? <>
            <Button variant="secondary" onClick={close} data-id="element-620" data-mp="360pw0pinhkgm">
              Cancel
            </Button>
            <Button variant="primary" onClick={toReview} data-id="element-621" data-mp="319zrfei1tlmz">
              Review
            </Button>
          </> : step === 'review' ? <>
            <Button variant="secondary" onClick={() => setStep('compose')} data-id="element-622" data-mp="2x7wd8i3zw2y0">
              Back
            </Button>
            <Button variant="primary" onClick={() => setStep('payment')} data-id="element-623" data-mp="3tseuknm2f560">
              Continue to payment
            </Button>
          </> : step === 'payment' ? <>
            <Button variant="secondary" onClick={() => setStep('review')} data-id="element-624" data-mp="39g9cm96qiz8i">
              Back
            </Button>
            <Button variant="primary" onClick={simulateProviderConfirmation} data-id="element-625" data-mp="3eeau4poiiqhv">
              Simulate provider confirmation
            </Button>
          </> : <Button variant="primary" onClick={close} data-id="element-626" data-mp="34izp4z2woxnb">
            Done
          </Button>} data-id="element-619" data-mp="2ic6pofj5vhr6">
      {step === 'compose' && <div className="space-y-4" data-id="element-627" data-mp="11r8gijx03u6">
          <div className="grid gap-4 sm:grid-cols-2" data-id="element-628" data-mp="10pkxelppovk">
            <Field label="Gift value" htmlFor="gift-amount" hint={`${koboToNaira(type.minValueKobo)} – ${koboToNaira(type.maxValueKobo)}`} data-id="element-629" data-mp="13lnxhcf33qv">
              <Input id="gift-amount" inputMode="decimal" value={amount} onChange={event => setAmount(event.target.value)} placeholder="10000" data-id="element-630" data-mp="3nez9bnpn6wwc" />
            </Field>
            <Field label="Recipient" htmlFor="gift-recipient" hint="Campuzon handle" data-id="element-631" data-mp="24hcpbrbe8f97">
              <Input id="gift-recipient" value={recipient} onChange={event => setRecipient(event.target.value)} placeholder="@mary.o" data-id="element-632" data-mp="1zije65jkx0c7" />
            </Field>
          </div>
          <Field label="Message (optional)" htmlFor="gift-message" data-id="element-633" data-mp="29bqgtimxq28l">
            <Textarea id="gift-message" rows={3} value={message} onChange={event => setMessage(event.target.value)} placeholder="Happy birthday!" data-id="element-634" data-mp="1lkbhzzuda323" />
          </Field>
          <p className="text-body-sm text-ink-muted" data-id="element-635" data-mp="1qj600zkifnl5">
            Recipients are addressed by handle. A bank account is never required to give a CampaGift,
            and the handle is never the financial identifier.
          </p>
          {error && <p className="flex items-start gap-2 rounded-md border border-danger-100 bg-danger-50 px-3 py-2 text-body-sm text-danger-700" data-id="element-636" data-mp="31xrytqrtsy9q">
              <AlertTriangleIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-637" data-mp="3e02qt79tne8y" />
              {error}
            </p>}
        </div>}

      {step === 'review' && <div className="space-y-4" data-id="element-638" data-mp="3ixkxer0d9hds">
          <dl className="divide-y divide-line rounded-card border border-line" data-id="element-639" data-mp="3nysa94hd96rb">
            <div className="flex items-center justify-between px-4 py-3" data-id="element-640" data-mp="2qb06tsrpt9ba">
              <dt className="text-body text-ink-muted" data-id="element-641" data-mp="2q8xbct7smnz6">Gift value</dt>
              <dd className="cz-num text-body font-semibold text-ink" data-id="element-642" data-mp="2vactodil6gdv">
                {koboToNaira(kobo ?? 0)}
              </dd>
            </div>
            <div className="flex items-center justify-between px-4 py-3" data-id="element-643" data-mp="34zecebginbvj">
              <dt className="text-body text-ink-muted" data-id="element-644" data-mp="351hazjvqidbv">
                Purchase fee ({bpsLabel(type.purchaseFeeBps)})
              </dt>
              <dd className="cz-num text-body text-ink" data-id="element-645" data-mp="2cik2mon2v58s">{koboToNaira(fee)}</dd>
            </div>
            <div className="flex items-center justify-between bg-canvas px-4 py-3" data-id="element-646" data-mp="toqytnfh7vlq">
              <dt className="text-body font-semibold text-ink" data-id="element-647" data-mp="pmx21q8ejbqu">Total to pay</dt>
              <dd className="cz-num text-h4 font-semibold text-ink" data-id="element-648" data-mp="pkt1ni9pouvb">{koboToNaira(total)}</dd>
            </div>
          </dl>

          <dl className="space-y-2 text-body-sm" data-id="element-649" data-mp="fozel4yd9iza">
            <div className="flex justify-between gap-4" data-id="element-650" data-mp="fo7v2si1nbpd">
              <dt className="text-ink-muted" data-id="element-651" data-mp="apmtaon58jyt">Recipient</dt>
              <dd className="text-ink" data-id="element-652" data-mp="ar7o8fdde4x7">@{recipient.replace(/^@/, '')}</dd>
            </div>
            <div className="flex justify-between gap-4" data-id="element-653" data-mp="5wjw86j5qsdv">
              <dt className="text-ink-muted" data-id="element-654" data-mp="1y4f2j9rce8d">Expiry</dt>
              <dd className="text-ink" data-id="element-655" data-mp="1up0dhjzx77h">
                {type.expiryDays ? `${type.expiryDays} days after issue` : 'No expiry'}
              </dd>
            </div>
            <div className="flex justify-between gap-4" data-id="element-656" data-mp="3o3rl7inf4e07">
              <dt className="text-ink-muted" data-id="element-657" data-mp="3j4oe7snvnxpl">Where it can be used</dt>
              <dd className="max-w-[24ch] text-right text-ink" data-id="element-658" data-mp="3j7iz1laszkjj">
                {type.eligibleCategoryIds === 'all' ? `${eligibleMerchants.length} participating merchants` : type.eligibleCategoryIds.join(', ')}
              </dd>
            </div>
            <div className="flex justify-between gap-4" data-id="element-659" data-mp="11vjyfoultfgj">
              <dt className="text-ink-muted" data-id="element-660" data-mp="16u3oo1g8ddep">Acceptance</dt>
              <dd className="text-ink" data-id="element-661" data-mp="16wpspr3wxas6">
                {type.acceptanceRequired ? 'Recipient must accept' : 'Delivered automatically'}
              </dd>
            </div>
          </dl>

          <div className="rounded-card border border-line bg-canvas px-4 py-3" data-id="element-662" data-mp="wytnffxnpa1z">
            <p className="text-body-sm font-semibold text-ink" data-id="element-663" data-mp="j6vh1cvbxs9i">Terms you are agreeing to</p>
            <ul className="mt-2 space-y-1.5" data-id="element-664" data-mp="o4x223cbg1nm">
              {type.terms.map(term => <li key={term} className="text-body-sm text-ink-muted" data-id="element-665" data-mp="98fs9gjryv36">
                  · {term}
                </li>)}
            </ul>
          </div>
        </div>}

      {step === 'payment' && <div className="space-y-4" data-id="element-666" data-mp="3qq3icbaqaa6z">
          <div className="rounded-card border border-warn-100 bg-warn-50 px-4 py-3" data-id="element-667" data-mp="3mohxo3rycrm8">
            <div className="flex flex-wrap items-center gap-2" data-id="element-668" data-mp="4ilrnyppkkbl">
              <Badge tone="warn" icon={<LockIcon aria-hidden className="h-4 w-4" data-id="element-670" data-mp="4g90cuy28xlv" />} data-id="element-669" data-mp="4k6ltn84218x">
                Purchase with real money is disabled
              </Badge>
            </div>
            <p className="mt-2 text-body-sm text-ink-muted" data-id="element-671" data-mp="di8jbssmybrg">
              The <code className="cz-num" data-id="element-672" data-mp="9hyckdx0rhbj">campa_gift_purchase</code> flag is off. Real-money issuance
              needs a licensed payment provider and the compliance gate cleared first.
            </p>
          </div>
          <ol className="space-y-2 text-body-sm text-ink-muted" data-id="element-673" data-mp="i9i8yte6pf3c">
            <li data-id="element-674" data-mp="iccrgfdn0pu4">1. Payment instruction sent to the licensed payment processor.</li>
            <li data-id="element-675" data-mp="x7j0zcbxz6s7">2. Provider confirms server-side by webhook, with its own reference.</li>
            <li data-id="element-676" data-mp="s9r403wfwuh5">3. Campuzon verifies the confirmation and checks the idempotency key.</li>
            <li data-id="element-677" data-mp="ve5wjslx3h9b">4. Only then is the instrument created and the ledger written.</li>
          </ol>
          <p className="text-body-sm text-ink" data-id="element-678" data-mp="ql33dll1y9tn">
            {purchaseEnabled ? 'Awaiting provider confirmation.' : 'Because that path is gated, the button below stands in for the provider confirmation so you can see the rest of the lifecycle. No money moves.'}
          </p>
          {error && <p className="flex items-start gap-2 rounded-md border border-danger-100 bg-danger-50 px-3 py-2 text-body-sm text-danger-700" data-id="element-679" data-mp="1e9cr4t1tt7nf">
              <AlertTriangleIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-680" data-mp="15a96vlqxam7c" />
              {error}
            </p>}
        </div>}

      {step === 'issued' && <div className="space-y-4" data-id="element-681" data-mp="3q0q8ck38wve6">
          <p className="flex items-start gap-2 text-body text-ink" data-id="element-682" data-mp="3q1rjeok9hhvw">
            <CheckCircle2Icon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-ok-700" data-id="element-683" data-mp="3l1dlw0gadqc4" />
            <span data-id="element-684" data-mp="20k4e6dfpyoso">
              {koboToNaira(kobo ?? 0)} {type.name} issued to @{recipient.replace(/^@/, '')} as{' '}
              <span className="cz-num font-semibold" data-id="element-685" data-mp="25i5z73wpgy6s">{issuedReference}</span>. They have been notified
              and can open it now.
            </span>
          </p>
          <div className="rounded-card border border-line bg-canvas px-4 py-3" data-id="element-686" data-mp="2eb960tcuicsg">
            <p className="text-body-sm text-ink-muted" data-id="element-687" data-mp="2eddmearxl49m">Redemption code (shown once)</p>
            <p className="cz-num mt-1 text-h4 font-semibold tracking-wide text-ink" data-id="element-688" data-mp="2ja40isk6dpse">{issuedCode}</p>
            <p className="mt-2 text-caption text-ink-muted" data-id="element-689" data-mp="2o7m0vuk4mwos">
              Campuzon keeps only a hash of this code. It cannot be recovered later — the holder can
              always request a fresh redemption token instead.
            </p>
          </div>
          <ul className="space-y-1.5 text-body-sm text-ink-muted" data-id="element-690" data-mp="yvaemtrg6vew">
            <li data-id="element-691" data-mp="12w42xo8syc8b">· Ledger: issuance recorded against the CampaGift outstanding-value account.</li>
            {fee > 0 && <li data-id="element-692" data-mp="266rntypns4xu">· Ledger: purchase fee of {koboToNaira(fee)} recorded to platform fees.</li>}
            <li data-id="element-693" data-mp="217wwg8dml824">· Receipt reference generated and attached to the instrument.</li>
          </ul>
        </div>}
    </Modal>;
}